'use client';

/**
 * **The Contacts drawer's record, rendered as attribute rows** (LF-6306 / [ATTR-5],
 * reconciled with story 7a by LF-6350 / [ATTR-8]).
 *
 * The conversion itself: [ATTR-4]'s contact specs laid out through [ATTR-2]'s
 * widened `AttributeGrid`, in the 7a "Groups: rule" format —
 * a small uppercase title, a hairline under it, **no card**
 * (`docs/attribute-treatment.md` §1.10; the Contacts drawer is white, so a white
 * card on it would be a border and nothing else).
 *
 * This component owns *host policy only* — which groups this surface is allowed to
 * show. Everything else is already built and is reached rather than restated:
 *
 * · **The group chrome** — title, hairline, the padding around it, the space
 *   between groups, the empty-group rule and the surface's selection colour — is
 *   {@link AttributeSections} (LF-6350). It used to be six hand-typed classes
 *   here, which is how the title's letter-spacing drifted from the prototype and
 *   the section padding lost a step with nothing to notice.
 * · **The field list** is [ATTR-4]'s pure builder, whose order and grouping are
 *   story 7a's.
 * · **The commit-on-blur save path** is §2's `useAttributeCommit`, reached through
 *   the grid and never reimplemented — see that hook's header for the four defects
 *   a second copy would reintroduce.
 * · **Row motion** on a `hidden` flip is [ATTR-3], wired onto the same `visible`
 *   derivation the commit hook indexes into.
 *
 * ## One grid per group, deliberately
 *
 * `Tab` therefore steps **within** a group and closes at its end rather than
 * crossing into the next one. That is the enquiry drawer's shipped behaviour
 * too (it renders three grids inside one card), and it follows from the grid
 * owning its own open-field state: one grid per group is what gives each group
 * its own heading and hairline. Threading one chain across four groups would
 * mean hoisting `useAttributeCommit` above the grid — a change to §2 code
 * neither this ticket nor [ATTR-5] has reason to make.
 */

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import {
  AttributeSections,
  type AttributeBlock,
  type AttributeSpec,
  type AutoOpenSignal
} from '@/components/attributes';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import {
  ADDRESS_LABEL,
  buildContactAttributeSpecs,
  INDIVIDUAL_NAME_LABEL,
  ORGANISATION_NAME_LABEL,
  PERSONAL_DETAILS_GROUP_TITLE,
  ROLE_GROUP_TITLE_ESTATE,
  ROLE_GROUP_TITLE_NON_ESTATE
} from './contact-attribute-specs';
import { useIdentityVariantFields } from '@/components/identity-variants';
import { useContactPmsAttributeBlocks } from './contact-pms-attribute-section';
import { isOrganisationContact } from '@/lib/services/contacts/utils';
import type { Contact } from '@/lib/services/contacts/types';
import {
  useAiReviewFieldMarking,
  type ReviewItemStatus
} from '@/components/ai-review/change-status';
import { isReviewerAdded } from '@/components/ai-review/compact/pending-id';

/**
 * `RoleSection`'s own gate, carried across verbatim: today's drawer passes
 * `showRoles={hasMatterId}`, so a **global** (non-matter) contact has no roles
 * surface at all.
 *
 * [ATTR-4]'s builder cannot express that on its own — it emits the role group
 * unconditionally, and for a non-estate contact that group is a single
 * `Role — Client` row with no `hidden` gate. Rendering it on the global contacts
 * page would put a row on screen that today's form does not show, against the
 * epic's "both hosts unchanged where not converted" contract. The gate is a
 * property of the *host*, not of the contact, which is why it lives here rather
 * than in the spec module.
 *
 * The title is story 7a's `Role in the estate` as of LF-6350, or its non-estate
 * counterpart `Role on this matter` (VALE-326) — both must track the builder's
 * group titles exactly, which is why this set is built from the same exported
 * constants rather than restating either string here.
 */
const MATTER_SCOPED_GROUPS = new Set([
  ROLE_GROUP_TITLE_ESTATE,
  ROLE_GROUP_TITLE_NON_ESTATE
]);

export interface ContactAttributeGridProps {
  contact: Contact;
  /** `isEstateAdminType(matter?.type)` — picks RoleSection's estate branch. */
  isEstate: boolean;
  /** `hasMatterId` — gates the per-matter Notes row and the role group. */
  hasMatterContext: boolean;
  /** An LPR on an enquiry — see `ContactAttributeSpecOptions.enquiryContact`. */
  enquiryContact?: boolean;
  /**
   * The matter this contact is being edited on, when there is one (VALE-158).
   *
   * Needed because a PMS binding is per-`(contact, matter)`: the participant-type
   * section reads the contact's stored selection from that matter's bindings and
   * unbinds against it. `hasMatterContext` alone cannot serve — it says *whether*
   * there is a matter, not *which*.
   */
  matterId?: number;
  /**
   * Commits one field's patch. **Must reject to surface a failure** (§2 — a
   * swallowed rejection is indistinguishable from a save that worked) and must
   * **not** close the drawer, which is what separates it from the whole-form
   * `onSave` the flag-off path uses.
   */
  onCommit: (patch: Partial<Contact>) => Promise<void>;
  /** Fires as a field opens and closes, so the drawer can keep `Escape`. */
  onEditingChange?: (editing: boolean) => void;
  /**
   * What the AI changed on this contact, when the drawer was opened from an
   * AI-extract review (VALE-1199).
   *
   * The review list inks a changed value and marks a new one; opening the
   * drawer used to drop all of it, so the reviewer lost the very distinction
   * they came to check. Absent everywhere else, and then nothing here changes.
   */
  changeStatus?: ReviewItemStatus;
  /** Which fields the reviewer has rejected, greyed here, as on the list. */
  acceptedChanges?: Record<string, boolean>;
  /** Toggle one field's reject, from the row's own popover (VALE-1208). */
  onToggleRejectChange?: (key: string) => void;
  /**
   * The contract keys the reviewer has already changed on this contact
   * (VALE-1264) — see `AiReviewFieldMarkingOptions.editedKeys`. Absent outside
   * a review, and then nothing here changes.
   */
  editedChangeKeys?: ReadonlySet<string>;
  /**
   * What each changed key held before the reviewer first touched it — on a
   * created contact, the AI's own value (VALE-1617). Lets `Use suggested
   * value` restore where the diff carries no proposal.
   */
  originalChangeValues?: ReadonlyMap<string, unknown>;
  /**
   * **Fires while a contact-type switch is pending completion** (LF-6374) — the
   * `Contact type` row has been changed, and the record cannot hold the new
   * type until the reader also fills in a name it does not yet have.
   *
   * The drawer wires this to its own "abandoning this must be a deliberate
   * act" guard: closing over a pending, incomplete switch is the exact defect
   * this ticket exists to fix, so the host needs to know while it is still
   * true, not just that a commit failed. Absent on hosts that have not wired
   * it, which simply keeps today's "close discards it" behaviour for them.
   */
  onPendingSwitchChange?: (pending: boolean) => void;
  /**
   * **This grid is rendering a record that does not exist yet** (VALE-632) —
   * the buffered create form, where `onCommit` merges into local state and one
   * `POST` behind the footer is the write.
   *
   * The grid itself needs no other change: `buildContactAttributeSpecs` is a
   * pure function of whatever record it is handed, so the buffer branches the
   * row list exactly as a fetched contact does, and the `Contact type` switch's
   * pending state resolves against the buffer the same way it resolves against
   * a refetched record (see {@link pendingType}). What this flag changes is
   * only what the *PMS* section may assume — see
   * `ContactPmsAttributeSectionOptions.addMode` — plus the cache seeding below,
   * which has no contact to seed against.
   */
  addMode?: boolean;
}

/** Stable identity, so the `useMemo` below is not invalidated every render. */
const NO_PMS_BLOCKS: AttributeBlock<Partial<Contact>>[] = [];
/*
  The identity-variant rows' own fail-closed value, for the same reason and at
  the same boundary as `NO_PMS_BLOCKS` — see `ContactAttributeGrid` below.
*/
const NO_VARIANT_FIELDS: AttributeSpec<Partial<Contact>>[] = [];

/**
 * Lays the identity block out as a 2×2 (VALE-863).
 *
 * ```
 *   Name          Address
 *   Other names   Other addresses
 * ```
 *
 * Each scalar sits above the collection that extends it, so a reader scanning
 * the left column gets every name and the right column every address — rather
 * than the pairs running the other way, where "other names" sat beside a name
 * and "other addresses" two lines below beside an address.
 *
 * **Position, not order-by-luck.** The grid flows fields into columns in array
 * order, so the block only holds together if the two variant rows land
 * immediately after `Address` *and* both left-hand rows declare `startsRow` —
 * `hidden` rows elsewhere in the section change the column parity at runtime,
 * and without the anchors, unticking `Deceased` re-pairs everything below it.
 *
 * The anchors are matched on the exported label constants rather than typed
 * literals, for the reason the auto-open signal is: they are matched on the
 * string, and a restated one goes stale silently when a label changes.
 *
 * A missing anchor is not an error — an organisation has no `Name` row — so an
 * unplaced variant row is dropped rather than appended somewhere arbitrary.
 *
 * Exported for its own spec: the placement is invisible to a render assertion in
 * a provider-less harness (the rows are not composed there at all), and it is
 * the rule this ticket actually changed.
 */
export function spliceVariantFields(
  fields: AttributeSpec<Partial<Contact>>[],
  {
    otherNamesField,
    otherAddressesField
  }: {
    otherNamesField: AttributeSpec<Partial<Contact>> | undefined;
    otherAddressesField: AttributeSpec<Partial<Contact>> | undefined;
  }
): AttributeSpec<Partial<Contact>>[] {
  // No pair to splice is the no-QueryClient host's answer, not an error.
  if (!otherNamesField || !otherAddressesField) return fields;
  const hasName = fields.some((field) => field.label === INDIVIDUAL_NAME_LABEL);
  const hasAddress = fields.some((field) => field.label === ADDRESS_LABEL);
  if (!hasName || !hasAddress) return fields;

  return fields.flatMap((field) => {
    if (field.label === INDIVIDUAL_NAME_LABEL) {
      return [{ ...field, startsRow: true }];
    }
    if (field.label === ADDRESS_LABEL) {
      // The whole second line follows the first, in one step.
      return [
        field,
        { ...otherNamesField, startsRow: true },
        otherAddressesField
      ];
    }
    return [field];
  });
}

/**
 * **The React-Query-dependent parts are only composed where React Query exists**
 * (VALE-158; the identity-variant rows joined them in VALE-863).
 *
 * This grid is rendered in trees that have **no** `QueryClientProvider` —
 * component-test harnesses and Storybook stories without the provider decorator
 * — and asking *"does this firm have a PMS?"* is impossible there. Its hooks
 * cannot be called conditionally, so the branch has to be a component boundary,
 * which is the same split (and the same reasoning) `PmsContactTypeSelector`
 * already makes for itself.
 *
 * **Fail-closed is also the honest answer**: "cannot determine the firm's PMS"
 * and "the firm has no catalogue" should look identical, and both mean no
 * section. Without this the grid threw `No QueryClient set` and took two
 * existing suites down with it — which is exactly what a Storybook story or a
 * lighter host would have hit in the same way.
 *
 * **`useIdentityVariantFields` sits behind the same gate, for the same reason**
 * (VALE-863). It opens a query and six mutations, so calling it in the base
 * grid re-broke exactly what this boundary was built to hold — four suites and
 * every provider-less Storybook story. Rows nobody can fetch are rows nobody
 * can add to, so omitting them is the honest render, not a degraded one.
 */
export function ContactAttributeGrid(props: ContactAttributeGridProps) {
  const queryClient = use(QueryClientContext);

  return queryClient ? (
    <ContactAttributeGridWithQueries {...props} />
  ) : (
    <ContactAttributeGridBase
      {...props}
      pmsBlocks={NO_PMS_BLOCKS}
      variantFields={NO_VARIANT_FIELDS}
    />
  );
}

function ContactAttributeGridWithQueries(props: ContactAttributeGridProps) {
  /*
    Composed here rather than in the spec builder because its gate is a *query*
    (`usePmsContactTypeSelection`), and `buildContactAttributeSpecs` is a pure
    function with no hooks by design — the same division `showGeneralNotes`
    already follows. Empty for a firm whose active PMS supplies no catalogue,
    which is most of them.
  */
  const { blocks: pmsBlocks, notePmsSelection } = useContactPmsAttributeBlocks({
    contact: props.contact,
    hasMatterContext: props.hasMatterContext,
    matterId: props.matterId,
    addMode: props.addMode
  });

  const { onCommit, addMode } = props;

  /**
   * **A participant-type commit has to tell the bindings query about itself.**
   *
   * That row reads its value from the bindings query, and a contact mutation
   * invalidates nothing PMS-related (`query-dependencies.ts` has no PMS
   * entry) — so without this the row reverted to its placeholder the instant
   * you chose a type, which is what UX testing found. See
   * {@link useContactPmsAttributeBlocks}'s `notePmsSelection` for why the
   * selection is seeded into the cache rather than held locally, and why the
   * bind outcome is deliberately *not* claimed here.
   *
   * **Only after the await resolves.** A rejected commit must leave the cache
   * untouched, or a failed save would render as a successful one — the exact
   * shape §2's "onSave must reject to surface a failure" contract exists to
   * prevent. The rejection still propagates, so the grid re-opens the field
   * with the typing intact.
   */
  const handleCommit = useCallback(
    async (patch: Partial<Contact>) => {
      await onCommit(patch);
      /*
        **Not on the create path** (VALE-632). The seed exists to stop the row
        reverting to its placeholder while the bindings query catches up — but
        it is keyed by `contact.id`, and a record being created has only a
        placeholder id. Seeding there would file a phantom binding against it.
        Nothing is lost: on the create form the row reads its selection back out
        of the buffer the commit just landed in, which is the same
        read-your-own-write the seed is imitating.
      */
      if (!addMode) notePmsSelection(patch);
    },
    [onCommit, notePmsSelection, addMode]
  );

  /*
    **The variant rows are fields, spliced beside the scalars they belong with**
    (VALE-863): `Name` | `Address` on one grid line, `Other names` | `Other
    addresses` on the next, so each collection sits under the scalar it extends
    — the column reading `spliceVariantFields` documents. They were a
    `component` block stacked under the
    grid, which is structurally incapable of that — a block is its own div and
    cannot interleave with the grid's cells — and reading them as a separate
    widget is exactly what the 27 Aug review picked up.

    Composed here, not in the base grid, because the hook opens a query: see the
    boundary doc on `ContactAttributeGrid`.
  */
  const variantFields = useIdentityVariantFields({
    ownerType: 'contact',
    ownerId: props.contact.id
  });

  return (
    <ContactAttributeGridBase
      {...props}
      onCommit={handleCommit}
      pmsBlocks={pmsBlocks}
      variantFields={variantFields}
    />
  );
}

function ContactAttributeGridBase({
  contact,
  isEstate,
  hasMatterContext,
  enquiryContact = false,
  onCommit,
  onEditingChange,
  changeStatus,
  acceptedChanges = {},
  onToggleRejectChange,
  editedChangeKeys,
  originalChangeValues,
  onPendingSwitchChange,
  pmsBlocks,
  variantFields
}: ContactAttributeGridProps & {
  pmsBlocks: AttributeBlock<Partial<Contact>>[];
  variantFields: AttributeSpec<Partial<Contact>>[];
}) {
  const { hasFeature } = useOptionalFeatureFlags();
  const showGeneralNotes = hasFeature('contacts-page');

  /**
   * **The type the reader has chosen, while the record cannot yet hold it**
   * (LF-6359 / [ATTR-9] §4) — only ever set for the direction that has to
   * wait (LF-6374); the direction the record can complete on its own sends a
   * real patch instead and never touches this state at all (see
   * `contact-attribute-specs.ts`'s `completesSwitchImmediately`).
   *
   * Nothing stores a contact's type — `isOrganisationContact` derives it from
   * whether `organisationName` is set — so choosing `Organisation` on a contact
   * that has no organisation name has nothing to write until that name is typed.
   * This is where the choice lives for that gap: the `Contact type` row commits
   * here (`localCommit`) rather than sending a patch, the builder re-branches
   * the row list, and the required name row's own commit is what persists the
   * type.
   *
   * `null` means *"read the type off the contact"* — at rest, and **also once
   * the completing row's commit lands**, even though `pending` itself is
   * never reset for that case (see {@link pendingType} below) — exactly the
   * same shape as the contact-id stamp this paragraph already describes.
   *
   * **It is stamped with the contact it was chosen on, and read back through
   * that stamp**, rather than being cleared by an effect when the drawer moves
   * to a different record. A pending choice belongs to the record it was made
   * on, so a stamp that no longer matches is simply not a pending choice — which
   * is a derivation, not a state transition, and needs no `setState` in an
   * effect (the ESLint rule is right: that costs a second render pass, and here
   * the first one would have rendered the wrong contact's branch).
   */
  const [pending, setPending] = useState<{
    contactId: number;
    type: 'individual' | 'organisation';
  } | null>(null);
  /*
    **Also derived, for the same reason** — the completing row's own commit
    (the organisation name, or the first/last name) refetches the contact, so
    `isOrganisationContact(contact)` agreeing with `pending.type` is itself the
    signal that there is nothing left pending. Reading that back out here,
    rather than resetting `pending` to `null` once it happens, is what keeps
    this a pure function of props and state instead of a second `setState`
    call racing the one `setPendingType` already made.
  */
  const pendingType =
    pending &&
    pending.contactId === contact.id &&
    (pending.type === 'organisation') !== isOrganisationContact(contact)
      ? pending.type
      : null;

  /**
   * A one-shot request to open the field that completes the pending switch —
   * `Organisation name` for the individual → organisation direction, the
   * individual `Name` row for the reverse when the record has no personal name
   * to fall back on either. `token` is bumped via the functional updater (never
   * a ref) every time {@link setPendingType} fires, so `AttributeGrid`
   * (via `AttributeSections`) opens it without the reader having to find a
   * new required row among the read-outs — the "lead the reader directly
   * into the field" half of the ticket's contract.
   *
   * **Both labels are imported, not typed** (VALE-863). The signal is matched
   * on the label string, so a literal here is a second definition that goes
   * stale silently — and it nearly did: consolidating the three name rows into
   * one renamed `First name` to `Name`, which would have left this pointing at
   * a row that no longer exists, with nothing failing to say so.
   */
  const [autoOpen, setAutoOpen] = useState<AutoOpenSignal | null>(null);

  const setPendingType = useCallback(
    (next: 'individual' | 'organisation') => {
      setPending({ contactId: contact.id, type: next });
      setAutoOpen((prev) => ({
        label:
          next === 'organisation'
            ? ORGANISATION_NAME_LABEL
            : INDIVIDUAL_NAME_LABEL,
        token: (prev?.token ?? 0) + 1
      }));
    },
    [contact.id]
  );

  useEffect(() => {
    onPendingSwitchChange?.(pendingType !== null);
  }, [pendingType, onPendingSwitchChange]);

  /**
   * **The AI review field-marking treatment, bound rather than restated**
   * (VALE-1249).
   *
   * This block used to carry its own ~180-line copy of the rules — the field
   * kind, the unset-flag rule, ink-versus-badge, the inline superseded value,
   * the popover and its keying, the reject and the clear routing into it. The
   * review list carried a second copy, and the two drifted twice (VALE-1208,
   * VALE-1247). One hook now owns all of it, including the hover state, so
   * there is one definition to fix the next time one of them is wrong.
   *
   * The scope prefix is this surface's own: the drawer renders exactly one of
   * these at a time, so the row label alone distinguishes every anchor on it.
   */
  const { mark: markChange } = useAiReviewFieldMarking({
    status: changeStatus,
    /* The drawer is the whole record: a note addition prints the note it lands after (VALE-1951). */
    showsRecordedNote: true,
    acceptedChanges,
    onToggleRejectChange,
    /*
      **Taking a reject back writes the AI's value back** (VALE-1617), through
      the same commit a row edit uses. The treatment decides which rows can
      honestly offer it and which say `Undo reject` instead.
    */
    onRestoreSuggested: (patch) => {
      void onCommit(patch as Parameters<typeof onCommit>[0]);
    },
    popoverScope: 'drawer',
    editedKeys: editedChangeKeys,
    originalValues: originalChangeValues,
    /* The reviewer's own row is new but has nothing to review (VALE-1617). */
    offersReview: !isReviewerAdded(contact)
  });

  /*
    Rebuilt from the latest contact snapshot on every render, which is what
    makes a conditional row react to the commit that changed its gate: ticking
    `Deceased` refetches the contact, the builder re-runs, `Date of death` stops
    being `hidden`, and [ATTR-3]'s motion animates it in.
  */
  const groups = useMemo(
    () =>
      buildContactAttributeSpecs(contact, {
        isEstate,
        hasMatterContext,
        showGeneralNotes,
        contactType: pendingType ?? undefined,
        onContactTypeChange: setPendingType,
        enquiryContact
      }).filter(
        (group) =>
          hasMatterContext ||
          // An enquiry's LPR carries its own role group, and has no matter.
          enquiryContact ||
          // A headless group (`title: null`) is never matter-scoped — the gate
          // is keyed on the heading, and a continuation has none.
          group.title === null ||
          !MATTER_SCOPED_GROUPS.has(group.title)
      ),
    [
      contact,
      isEstate,
      hasMatterContext,
      showGeneralNotes,
      pendingType,
      setPendingType,
      enquiryContact
    ]
  );

  /*
    [ATTR-4]'s builder still returns the pre-widening `{ title, fields }[]`
    shape (`ContactAttributeGroup`) — that module's own contract, unrelated to
    this ticket's scope. `AttributeSections` now takes the [INVDRAW-2] block
    union, so each group becomes exactly the `rows` block it already was: same
    title, same fields, no `columns` override (this call site never set one),
    which is what keeps this a mechanical, zero-pixel-diff adaptation.
  */
  // Composed by the caller, which is where the query lives — the boundary doc
  // on `ContactAttributeGrid` says why. Empty on a provider-less host.
  const [otherNamesField, otherAddressesField] = variantFields;

  /**
   * Mark the rows the AI touched (VALE-1199).
   *
   * Rides `display` and `badge`, so the row stays exactly the row it was —
   * same editor, same commit, same everything — and simply says what happened
   * to it. Which contract keys a row writes is asked of its own `toPatch`,
   * never restated as a label map here.
   *
   * **All of that now lives in the treatment** (VALE-1249). What is left here
   * is this surface's own shape: the drawer marks every group's fields in
   * place, because it is a record editor and renders every row whether the AI
   * touched it or not. `mark` is the identity function when there is nothing to
   * mark, so the no-diff case costs a map and changes nothing.
   */
  const marked = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        fields: group.fields.map((field) => markChange(field))
      })),
    [groups, markChange]
  );

  const blocks = useMemo(
    () => [
      ...marked.map((group) => ({
        kind: 'rows' as const,
        title: group.title,
        fields:
          group.title === PERSONAL_DETAILS_GROUP_TITLE
            ? spliceVariantFields(group.fields, {
                otherNamesField,
                otherAddressesField
              })
            : group.fields
      })) /*
        Appended last, after Notes. The section belongs to an *external system*
        rather than to the record, so it reads as a footer to the contact rather
        than as one of the contact's own facts — and putting it last means the
        record's own reading order (story 7a's) is untouched for the firms that
        never see it at all.
      */,
      ...pmsBlocks
    ],
    [marked, pmsBlocks, otherNamesField, otherAddressesField]
  );

  return (
    <AttributeSections<Partial<Contact>>
      blocks={blocks}
      onSave={onCommit}
      onEditingChange={onEditingChange}
      autoOpen={autoOpen}
      /*
        **Two props used to be passed here and are now the grid's own defaults**
        (LF-6388 / [ATTR-10]): `booleanControl="segmented"` — story 7a's live
        `Yes` / `No` pair — and `refined`, which put the hover tint and the copy
        / clear affordances on the value cell, gave the open editor a ring
        instead of a white bordered `Input`, and opened `date` rows in the
        `DatePicker`.

        Both were opt-**in** because each earlier ATTR ticket's contract was that
        no shipped enquiry pixel moves, which left the Contacts drawer as the
        only surface asking for the treatment the whole epic was building.
        [ATTR-10] settles it the other way: there is one treatment per field
        type and it is the default. So this call site now says nothing about
        chrome at all — which is exactly the point. The enquiries drawer renders
        the same way because it renders the same component, not because someone
        remembered a prop.
      */
      testId="contact-attribute-grid"
      fieldsTestIdPrefix="contact-attribute-fields"
    />
  );
}
